import { useMemo } from "react";

import moment from "moment";

import { LOCALE_OPTIONS } from "~/v2/constants/optionOrder";
import type { CombinedLeg } from "../utils";

export interface BidAskMidResult {
  bid: string;
  ask: string;
  mid: string;
  time: string;
}

export const getBidAskMid = (legs: CombinedLeg[]) => {
  const {
    bidOfBuys,
    bidOfSells,
    askOfBuys,
    askOfSells,
    midOfBuys,
    midOfSells,
  } = legs.reduce(
    (acc, leg) => {
      const {
        data: { contract },
        form: { positionIntent, ratioQty },
      } = leg;

      const side = positionIntent.includes("buy") ? "buy" : "sell";
      const ratio = Number.isNaN(ratioQty) ? 0 : ratioQty;

      return {
        bidOfBuys:
          acc.bidOfBuys +
          (side === "buy"
            ? (contract?.snapshot?.latestQuote?.bp ?? 0) * ratio
            : 0),
        bidOfSells:
          acc.bidOfSells +
          (side === "sell"
            ? (contract?.snapshot?.latestQuote?.bp ?? 0) * ratio
            : 0),
        askOfBuys:
          acc.askOfBuys +
          (side === "buy"
            ? (contract?.snapshot?.latestQuote?.ap ?? 0) * ratio
            : 0),
        askOfSells:
          acc.askOfSells +
          (side === "sell"
            ? (contract?.snapshot?.latestQuote?.ap ?? 0) * ratio
            : 0),
        midOfBuys:
          acc.midOfBuys +
          (side === "buy"
            ? (((contract?.snapshot?.latestQuote?.bp ?? 0) +
                (contract?.snapshot?.latestQuote?.ap ?? 0)) /
                2) *
              ratio
            : 0),
        midOfSells:
          acc.midOfSells +
          (side === "sell"
            ? (((contract?.snapshot?.latestQuote?.bp ?? 0) +
                (contract?.snapshot?.latestQuote?.ap ?? 0)) /
                2) *
              ratio
            : 0),
      };
    },
    {
      bidOfBuys: 0,
      bidOfSells: 0,
      askOfBuys: 0,
      askOfSells: 0,
      midOfBuys: 0,
      midOfSells: 0,
    },
  );

  const lastSnapshot = legs.reduce((acc, leg) => {
    const snapshotTime = leg.data.contract.snapshot?.latestQuote?.t;
    if (!snapshotTime) {
      return acc;
    }
    if (acc === "" || moment(snapshotTime).isAfter(acc)) {
      return snapshotTime;
    }
    return acc;
  }, "");

  const bid = bidOfBuys - askOfSells;
  const ask = askOfBuys - bidOfSells;
  const mid = midOfBuys - midOfSells;
  const isMidLessThanZero = mid < 0;

  return {
    bid: isMidLessThanZero ? ask : bid,
    ask: isMidLessThanZero ? bid : ask,
    mid,
    time:
      lastSnapshot === ""
        ? ""
        : moment(lastSnapshot).format("MM/DD/YYYY HH:mm:ss"),
  };
};

export const useBidAskMid = (legs: CombinedLeg[]): BidAskMidResult => {
  return useMemo(() => {
    const { bid, ask, mid, time } = getBidAskMid(legs);

    return {
      bid: bid.toLocaleString("en-US", LOCALE_OPTIONS),
      ask: ask.toLocaleString("en-US", LOCALE_OPTIONS),
      mid: mid.toLocaleString("en-US", LOCALE_OPTIONS),
      time,
    };
  }, [legs]);
};
